import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smile, Paperclip, Download, X, ChevronDown, AlertCircle, Check, Clock } from 'lucide-react';

const EMOJIS = ['👍', '🙏', '✅', '💰', '🪙', '📸', '🧾', '😊', '👋', '🔥', '💯', '⏳', '📦', '🏦', '⚠️', '❓', '👌', '🤝', '✨', '🎮', '📱', '💳', '📝', '🚀'];

// Bouton smiley fonctionnel : petite grille d'emojis insérée dans l'input
export const EmojiButton = ({ onPick, title = 'Insérer un emoji' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={title}
        aria-label={title}
        className="p-2 hover:bg-gray-800 rounded-lg transition-colors flex-shrink-0"
      >
        <Smile size={20} className="text-gray-400" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="absolute bottom-11 right-0 z-30 w-64 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl p-2 grid grid-cols-8 gap-1"
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
          >
            {EMOJIS.map(e => (
              <button
                key={e}
                type="button"
                onClick={() => { onPick(e); setOpen(false); }}
                className="text-xl p-1 hover:bg-gray-700 rounded-lg transition-colors"
              >
                {e}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Aperçu plein écran d'une image envoyée
export const Lightbox = ({ src, name, onClose }) => (
  <AnimatePresence>
    {src && (
      <motion.div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <button
          onClick={onClose}
          aria-label="Fermer l'aperçu"
          className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors"
        >
          <X size={20} />
        </button>
        <motion.img
          src={src}
          alt={name || 'Image'}
          className="max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        />
        {name && <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-gray-400">{name}</p>}
      </motion.div>
    )}
  </AnimatePresence>
);

// Rendu d'une pièce jointe : miniature d'image (lightbox au clic) ou lien fichier
export const Attachment = ({ message, darkText, onPreview }) => {
  const src = message.fileUrl || message.fileData;
  if (!src) return null;
  const isImage = (message.fileType || '').startsWith('image/') || (message.fileData || '').startsWith('data:image');

  if (isImage) {
    return (
      <button type="button" onClick={() => onPreview(src, message.fileName)} className="block mb-2">
        <img src={src} alt={message.fileName || 'Image envoyée'} className="rounded-lg max-w-full max-h-48 hover:opacity-90 transition-opacity" />
      </button>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className={`flex items-center gap-2 mb-2 underline text-xs sm:text-sm ${darkText ? 'text-black' : 'text-yellow-400'}`}
    >
      {message.fileType === 'application/pdf' ? <Download size={14} /> : <Paperclip size={14} />}
      {message.fileName || 'Fichier joint'}
    </a>
  );
};

// Pastille "nouveaux messages" quand l'utilisateur a remonté le fil
export const NewMessagesPill = ({ visible, onClick, count }) => (
  <AnimatePresence>
    {visible && (
      <motion.button
        type="button"
        onClick={onClick}
        className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-yellow-500 text-black text-xs font-bold px-3 py-1.5 rounded-full shadow-lg"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
      >
        <ChevronDown size={14} />
        {count > 1 ? `${count} nouveaux messages` : 'Nouveau message'}
      </motion.button>
    )}
  </AnimatePresence>
);

// Statut d'envoi d'un message : en cours / envoyé / échec
export const SendStatus = ({ status, onRetry }) => {
  if (status === 'sending') return <Clock size={12} className="opacity-70" />;
  if (status === 'error') {
    return (
      <button type="button" onClick={onRetry} className="flex items-center gap-1 text-red-900 bg-red-200/90 rounded px-1" title="Réessayer">
        <AlertCircle size={12} />
        <span className="text-[10px] font-bold">Renvoyer</span>
      </button>
    );
  }
  return <Check size={12} className="opacity-70" />;
};
